// src/components/karigar/JobMaterialsTab.jsx
// UPDATED VERSION - Complete Materials CRUD Management
//
// FEATURES:
// 1. Issue Material → Admin issues gold/silver to karigar
// 2. Edit Material → Update issued material details
// 3. Delete Material → Remove issued material
// 4. Confirm Receipt → Mark material received by karigar
// 5. Undo Receipt → Undo karigar receipt confirmation
// 6. Record Return → Record finished product + unused material
// 7. Adjust Return → Edit return record
// 8. Reset Return → Delete return record
// 9. View Wastage → System calculates and displays wastage

import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import { Label } from "@/components/ui-v2/label";
import { Textarea } from "@/components/ui-v2/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui-v2/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui-v2/dropdown-menu";
import { Progress } from "@/components/ui-v2/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui-v2/tooltip";
import {
  Box,
  Plus,
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Scale,
  Gem,
  RefreshCw,
  Trash2,
  Eye,
  Clock,
  TrendingDown,
  MoreHorizontal,
  Edit,
  Undo2,
  RotateCcw,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

// ============================================================================
// CONSTANTS
// ============================================================================

const METAL_TYPES = [
  { value: "GOLD_24K", label: "Gold 24K (999)" },
  { value: "GOLD_22K", label: "Gold 22K (916)" },
  { value: "GOLD_18K", label: "Gold 18K (750)" },
  { value: "GOLD_14K", label: "Gold 14K (585)" },
  { value: "SILVER_999", label: "Silver 999 (Fine)" },
  { value: "SILVER_925", label: "Silver 925 (Sterling)" },
  { value: "PLATINUM_950", label: "Platinum 950" },
  { value: "PLATINUM_900", label: "Platinum 900" },
];

const METAL_PURITY_MAP = {
  GOLD_24K: 99.9,
  GOLD_22K: 91.6,
  GOLD_18K: 75.0,
  GOLD_14K: 58.5,
  SILVER_999: 99.9,
  SILVER_925: 92.5,
  PLATINUM_950: 95.0,
  PLATINUM_900: 90.0,
};

const STONE_TYPES = [
  { value: "DIAMOND", label: "Diamond" },
  { value: "RUBY", label: "Ruby" },
  { value: "EMERALD", label: "Emerald" },
  { value: "SAPPHIRE", label: "Sapphire" },
  { value: "PEARL", label: "Pearl" },
  { value: "CUBIC_ZIRCONIA", label: "Cubic Zirconia" },
  { value: "POLKI", label: "Polki" },
  { value: "KUNDAN", label: "Kundan" },
  { value: "OTHER", label: "Other" },
];

const WASTAGE_THRESHOLDS = {
  EXCELLENT: 2,
  GOOD: 3,
  ACCEPTABLE: 5,
  HIGH: 8,
  CRITICAL: 100,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatWeight = (weight) => {
  if (!weight && weight !== 0) return "-";
  return `${parseFloat(weight).toFixed(3)}g`;
};

const calculateWastagePercentage = (issued, returned) => {
  if (!issued || issued === 0) return 0;
  const wastage = issued - returned;
  return ((wastage / issued) * 100).toFixed(2);
};

const getWastageStatus = (percentage) => {
  const pct = parseFloat(percentage);
  if (pct < WASTAGE_THRESHOLDS.EXCELLENT)
    return { label: "Excellent", color: "bg-green-100 text-green-800" };
  if (pct < WASTAGE_THRESHOLDS.GOOD)
    return { label: "Good", color: "bg-blue-100 text-blue-800" };
  if (pct < WASTAGE_THRESHOLDS.ACCEPTABLE)
    return { label: "Acceptable", color: "bg-yellow-100 text-yellow-800" };
  if (pct < WASTAGE_THRESHOLDS.HIGH)
    return { label: "High", color: "bg-orange-100 text-orange-800" };
  return { label: "Critical", color: "bg-red-100 text-red-800" };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const JobMaterialsTab = ({
  job,
  currentJobMaterials,
  loading,
  // Existing handlers
  onIssueMaterial,
  onConfirmReceived,
  onRecordReturn,
  // NEW: CRUD handlers
  onUpdateMaterial,
  onDeleteMaterial,
  onUndoReceived,
  onUpdateReturn,
  onDeleteReturn,
  // Permissions from backend (optional - will fallback to status-based)
  materialPermissions,
  onRefresh,
}) => {
  // Dialog states
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showEditReturnDialog, setShowEditReturnDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUndoReceiptDialog, setShowUndoReceiptDialog] = useState(false);
  const [showResetReturnDialog, setShowResetReturnDialog] = useState(false);

  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [undoReason, setUndoReason] = useState("");
  const [resetReturnReason, setResetReturnReason] = useState("");
  const [adjustReturnReason, setAdjustReturnReason] = useState("");
  const [forceAction, setForceAction] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Determine permissions - use backend permissions if available, else fallback
  const canIssueMaterial =
    materialPermissions?.canIssueMaterial ??
    [
      "PENDING", // ✅ Can issue before assignment
      "ASSIGNED", // ✅ Can issue after assignment
      "ACCEPTED", // ✅ Can issue after acceptance
      "IN_PROGRESS", // ✅ Can issue during work
      "ON_HOLD", // ✅ Can issue while on hold
      "REVISION_REQUIRED", // ✅ Can issue for revision work
    ].includes(job?.status);

  const canRecordReturn =
    materialPermissions?.canRecordReturn ??
    [
      "IN_PROGRESS", // ✅ Can return during work
      "ON_HOLD", // ✅ Can return while on hold
      "QUALITY_CHECK", // ✅ Can return during QC
      "REVISION_REQUIRED", // ✅ Can return after revision
      "COMPLETED", // ✅ Can record late returns
    ].includes(job?.status);

  const canEditMaterials =
    materialPermissions?.canEditMaterials ??
    !["DELIVERED", "CANCELLED"].includes(job?.status); // Only terminal states block editing

  // ✅ FIXED: Only truly terminal states
  const isJobActive = !["DELIVERED", "CANCELLED"].includes(job?.status);

  // Get materials data
  const issuedMaterials =
    job?.materialsIssued || currentJobMaterials?.issuedMaterials || [];
  const materialSummary = currentJobMaterials?.materialSummary || null;
  const wastageData = currentJobMaterials?.wastage || null;

  // Get return record
  const returnRecord =
    job?.materialsReturned || currentJobMaterials?.returnedMaterials || null;
  const finishedWeight =
    returnRecord?.finishedWeight || job?.completionDetails?.finalWeight || 0;
  const unusedMetalReturned =
    returnRecord?.unusedMetal || materialSummary?.unusedMetalReturned || 0;

  // Calculate totals
  const totalIssuedWeight = issuedMaterials.reduce(
    (sum, m) => sum + (m.metalWeight || 0),
    0
  );
  const totalReturnedWeight = finishedWeight + unusedMetalReturned;

  const wastageWeight =
    returnRecord?.wastageWeight ?? totalIssuedWeight - totalReturnedWeight;
  const wastagePercentage =
    returnRecord?.wastagePercentage ??
    calculateWastagePercentage(totalIssuedWeight, totalReturnedWeight);

  const hasMaterialsReturned =
    currentJobMaterials?.stats?.hasMaterialsReturned ||
    finishedWeight > 0 ||
    returnRecord !== null;

  const allMaterialsReceived =
    issuedMaterials.length > 0 &&
    issuedMaterials.every((m) => m.receivedByKarigar);
  const pendingReceiptCount = issuedMaterials.filter(
    (m) => !m.receivedByKarigar
  ).length;

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleIssueMaterial = async (data) => {
    setSubmitting(true);
    try {
      await onIssueMaterial(job._id, data);
      setShowIssueDialog(false);
      toast.success("Material issued successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to issue material");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMaterial = async (data) => {
    if (!selectedMaterial || !onUpdateMaterial) return;
    setSubmitting(true);
    try {
      await onUpdateMaterial(job._id, selectedMaterial._id, data, forceAction);
      setShowEditDialog(false);
      setSelectedMaterial(null);
      setForceAction(false);
      toast.success("Material updated successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to update material");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMaterial = async () => {
    if (!selectedMaterial || !onDeleteMaterial) return;
    setSubmitting(true);
    try {
      await onDeleteMaterial(
        job._id,
        selectedMaterial._id,
        deleteReason,
        forceAction
      );
      setShowDeleteDialog(false);
      setSelectedMaterial(null);
      setDeleteReason("");
      setForceAction(false);
      toast.success("Material removed successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to delete material");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReceived = async () => {
    if (!selectedMaterial) return;
    setSubmitting(true);
    try {
      await onConfirmReceived(job._id, selectedMaterial._id);
      setShowConfirmDialog(false);
      setSelectedMaterial(null);
      toast.success("Material receipt confirmed");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to confirm receipt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndoReceived = async () => {
    if (!selectedMaterial || !onUndoReceived) return;
    setSubmitting(true);
    try {
      await onUndoReceived(job._id, selectedMaterial._id, undoReason);
      setShowUndoReceiptDialog(false);
      setSelectedMaterial(null);
      setUndoReason("");
      toast.success("Receipt confirmation undone");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to undo receipt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordReturn = async (data) => {
    setSubmitting(true);
    try {
      await onRecordReturn(job._id, data);
      setShowReturnDialog(false);
      toast.success("Material return recorded successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to record return");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateReturn = async (data) => {
    if (!onUpdateReturn) return;
    setSubmitting(true);
    try {
      await onUpdateReturn(job._id, data, adjustReturnReason);
      setShowEditReturnDialog(false);
      setAdjustReturnReason("");
      toast.success("Return record adjusted successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to adjust return");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetReturn = async () => {
    if (!onDeleteReturn) return;
    setSubmitting(true);
    try {
      await onDeleteReturn(job._id, resetReturnReason);
      setShowResetReturnDialog(false);
      setResetReturnReason("");
      toast.success("Return record reset successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Failed to reset return");
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit dialog with material data
  const openEditDialog = (material) => {
    setSelectedMaterial(material);
    setShowEditDialog(true);
  };

  // Open delete dialog
  const openDeleteDialog = (material) => {
    setSelectedMaterial(material);
    setDeleteReason("");
    setForceAction(false);
    setShowDeleteDialog(true);
  };

  // Open undo receipt dialog
  const openUndoReceiptDialog = (material) => {
    setSelectedMaterial(material);
    setUndoReason("");
    setShowUndoReceiptDialog(true);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Issued */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Issued</p>
                <p className="text-2xl font-bold">
                  {formatWeight(totalIssuedWeight)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <ArrowRight className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {issuedMaterials.length} material(s) issued
            </p>
          </CardContent>
        </Card>

        {/* Total Returned */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Returned</p>
                <p className="text-2xl font-bold">
                  {formatWeight(totalReturnedWeight)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <ArrowLeft className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Product + Unused
            </p>
          </CardContent>
        </Card>

        {/* Wastage */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Wastage</p>
                <p className="text-2xl font-bold">
                  {/* ✅ Only show wastage AFTER materials returned */}
                  {hasMaterialsReturned
                    ? formatWeight(wastageWeight > 0 ? wastageWeight : 0)
                    : "-"}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-amber-600" />
              </div>
            </div>
            {/* ✅ Only show badge AFTER materials returned */}
            {totalIssuedWeight > 0 && hasMaterialsReturned ? (
              <Badge
                className={`mt-1 ${getWastageStatus(wastagePercentage).color}`}
              >
                {typeof wastagePercentage === "number"
                  ? wastagePercentage.toFixed(2)
                  : wastagePercentage}
                % - {getWastageStatus(wastagePercentage).label}
              </Badge>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                {issuedMaterials.length > 0
                  ? "Pending return"
                  : "No materials issued"}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Receipt Status */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Receipt Status</p>
                <p className="text-2xl font-bold">
                  {issuedMaterials.length > 0
                    ? `${issuedMaterials.length - pendingReceiptCount}/${
                        issuedMaterials.length
                      }`
                    : "0/0"}
                </p>
              </div>
              <div
                className={`h-10 w-10 rounded-full flex items-center justify-center ${
                  allMaterialsReceived ? "bg-green-100" : "bg-yellow-100"
                }`}
              >
                {allMaterialsReceived ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <Clock className="h-5 w-5 text-yellow-600" />
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {allMaterialsReceived
                ? "All received"
                : `${pendingReceiptCount} pending`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      {isJobActive && (
        <div className="flex gap-3 flex-wrap">
          {canIssueMaterial && (
            <Button onClick={() => setShowIssueDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Issue Material
            </Button>
          )}
          {canRecordReturn &&
            issuedMaterials.length > 0 &&
            !hasMaterialsReturned && (
              <Button
                variant="outline"
                onClick={() => setShowReturnDialog(true)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Record Return
              </Button>
            )}
          {hasMaterialsReturned && canEditMaterials && onUpdateReturn && (
            <Button
              variant="outline"
              onClick={() => setShowEditReturnDialog(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Adjust Return
            </Button>
          )}
          {hasMaterialsReturned && canEditMaterials && onDeleteReturn && (
            <Button
              variant="ghost"
              onClick={() => setShowResetReturnDialog(true)}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Return
            </Button>
          )}
          <Button variant="ghost" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      )}

      {/* Issued Materials Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-4 w-4" />
            Issued Materials
          </CardTitle>
          <CardDescription>
            Materials issued to karigar for this job
          </CardDescription>
        </CardHeader>
        <CardContent>
          {issuedMaterials.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead className="text-right">Weight</TableHead>
                  <TableHead className="text-right">Purity</TableHead>
                  <TableHead>Issued Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issuedMaterials.map((material, idx) => (
                  <TableRow key={material._id || idx}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded bg-amber-100 flex items-center justify-center">
                          <Scale className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {material.metalType?.replace(/_/g, " ") || "Metal"}
                          </p>
                          {material.lotNumber && (
                            <p className="text-xs text-muted-foreground">
                              Lot: {material.lotNumber}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatWeight(material.metalWeight)}
                    </TableCell>
                    <TableCell className="text-right">
                      {material.metalPurity
                        ? `${material.metalPurity}%`
                        : METAL_PURITY_MAP[material.metalType]
                        ? `${METAL_PURITY_MAP[material.metalType]}%`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {material.issuedAt
                        ? format(new Date(material.issuedAt), "dd MMM yyyy")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          material.receivedByKarigar
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }
                      >
                        {material.receivedByKarigar ? "Received" : "Pending"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* View Details */}
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedMaterial(material);
                              setShowDetailsDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>

                          {/* Confirm Receipt - Only if not received */}
                          {!material.receivedByKarigar && isJobActive && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedMaterial(material);
                                setShowConfirmDialog(true);
                              }}
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Confirm Receipt
                            </DropdownMenuItem>
                          )}

                          {/* Undo Receipt - Only if received and handler exists */}
                          {material.receivedByKarigar &&
                            canEditMaterials &&
                            onUndoReceived && (
                              <DropdownMenuItem
                                onClick={() => openUndoReceiptDialog(material)}
                              >
                                <Undo2 className="h-4 w-4 mr-2" />
                                Undo Receipt
                              </DropdownMenuItem>
                            )}

                          {canEditMaterials &&
                            (onUpdateMaterial || onDeleteMaterial) && (
                              <DropdownMenuSeparator />
                            )}

                          {/* Edit Material */}
                          {canEditMaterials && onUpdateMaterial && (
                            <DropdownMenuItem
                              onClick={() => openEditDialog(material)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Material
                            </DropdownMenuItem>
                          )}

                          {/* Delete Material */}
                          {canEditMaterials && onDeleteMaterial && (
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(material)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Material
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Box className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No materials issued yet</p>
              {canIssueMaterial && (
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={() => setShowIssueDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Issue First Material
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stones Section */}
      {issuedMaterials.some((m) => m.stones?.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gem className="h-4 w-4" />
              Stones Issued
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {issuedMaterials.flatMap(
                (m, mIdx) =>
                  m.stones?.map((stone, sIdx) => (
                    <div
                      key={`${mIdx}-${sIdx}`}
                      className="p-3 bg-muted/50 rounded-lg"
                    >
                      <p className="font-medium">
                        {stone.type?.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Qty: {stone.quantity || 1}
                      </p>
                      {stone.weight && (
                        <p className="text-sm text-muted-foreground">
                          {stone.weight} carats
                        </p>
                      )}
                    </div>
                  )) || []
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Return Summary */}
      {hasMaterialsReturned && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                <CheckCircle className="h-4 w-4" />
                Material Return Summary
              </CardTitle>
              {canEditMaterials && (onUpdateReturn || onDeleteReturn) && (
                <div className="flex gap-2">
                  {onUpdateReturn && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowEditReturnDialog(true)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Adjust Return</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {onDeleteReturn && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowResetReturnDialog(true)}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Reset Return</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  Finished Product
                </p>
                <p className="text-xl font-bold text-green-800">
                  {formatWeight(finishedWeight)}
                </p>
              </div>
              {unusedMetalReturned > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Unused Metal</p>
                  <p className="text-xl font-bold">
                    {formatWeight(unusedMetalReturned)}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Final Purity</p>
                <p className="text-xl font-bold">
                  {returnRecord?.finishedPurity ||
                    job?.completionDetails?.finalPurity ||
                    "-"}
                </p>
              </div>
              {(returnRecord?.hallmarkNumber ||
                job?.completionDetails?.hallmarkNumber) && (
                <div>
                  <p className="text-sm text-muted-foreground">Hallmark No.</p>
                  <p className="text-xl font-bold">
                    {returnRecord?.hallmarkNumber ||
                      job?.completionDetails?.hallmarkNumber}
                  </p>
                </div>
              )}
            </div>

            {/* Wastage Details */}
            {totalIssuedWeight > 0 && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Wastage Analysis</span>
                  <Badge className={getWastageStatus(wastagePercentage).color}>
                    {typeof wastagePercentage === "number"
                      ? wastagePercentage.toFixed(2)
                      : wastagePercentage}
                    % wastage
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span>
                    Issued: <strong>{formatWeight(totalIssuedWeight)}</strong>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <span>
                    Returned:{" "}
                    <strong>{formatWeight(totalReturnedWeight)}</strong>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <span className="text-amber-700">
                    Wastage:{" "}
                    <strong>
                      {formatWeight(wastageWeight > 0 ? wastageWeight : 0)}
                    </strong>
                  </span>
                </div>
                <Progress
                  value={100 - parseFloat(wastagePercentage)}
                  className="h-2 mt-2"
                />

                {returnRecord?.returnedAt && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Returned on:{" "}
                    {format(
                      new Date(returnRecord.returnedAt),
                      "dd MMM yyyy, hh:mm a"
                    )}
                    {returnRecord.receivedByName &&
                      ` • Received by: ${returnRecord.receivedByName}`}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============================================================================ */}
      {/* DIALOGS */}
      {/* ============================================================================ */}

      {/* Issue Material Dialog */}
      <IssueMaterialDialog
        open={showIssueDialog}
        onOpenChange={setShowIssueDialog}
        onSubmit={handleIssueMaterial}
        submitting={submitting}
        job={job}
      />

      {/* Edit Material Dialog */}
      <EditMaterialDialog
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) setSelectedMaterial(null);
        }}
        onSubmit={handleUpdateMaterial}
        submitting={submitting}
        material={selectedMaterial}
        isReceived={selectedMaterial?.receivedByKarigar}
        forceAction={forceAction}
        setForceAction={setForceAction}
      />

      {/* Record Return Dialog */}
      <RecordReturnDialog
        open={showReturnDialog}
        onOpenChange={setShowReturnDialog}
        onSubmit={handleRecordReturn}
        submitting={submitting}
        job={job}
        totalIssuedWeight={totalIssuedWeight}
      />

      {/* Edit Return Dialog */}
      <EditReturnDialog
        open={showEditReturnDialog}
        onOpenChange={setShowEditReturnDialog}
        onSubmit={handleUpdateReturn}
        submitting={submitting}
        returnRecord={returnRecord}
        totalIssuedWeight={totalIssuedWeight}
        adjustReason={adjustReturnReason}
        setAdjustReason={setAdjustReturnReason}
      />

      {/* Confirm Receipt Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Material Receipt</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm that karigar has received this material?
              {selectedMaterial && (
                <div className="mt-3 p-3 bg-muted rounded">
                  <p className="font-medium">
                    {selectedMaterial.metalType?.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm">
                    Weight: {formatWeight(selectedMaterial.metalWeight)}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReceived}
              disabled={submitting}
            >
              {submitting ? "Confirming..." : "Confirm Receipt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Undo Receipt Dialog */}
      <AlertDialog
        open={showUndoReceiptDialog}
        onOpenChange={setShowUndoReceiptDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo Material Receipt</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the material as not yet received by karigar.
              {selectedMaterial && (
                <div className="mt-3 p-3 bg-muted rounded">
                  <p className="font-medium">
                    {selectedMaterial.metalType?.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm">
                    Weight: {formatWeight(selectedMaterial.metalWeight)}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="undoReason">Reason (Optional)</Label>
            <Textarea
              id="undoReason"
              value={undoReason}
              onChange={(e) => setUndoReason(e.target.value)}
              placeholder="Why is this being undone?"
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndoReceived}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? "Processing..." : "Undo Receipt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Material Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Material</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this material from the job. This action cannot be
              easily undone.
              {selectedMaterial && (
                <div className="mt-3 p-3 bg-muted rounded">
                  <p className="font-medium">
                    {selectedMaterial.metalType?.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm">
                    Weight: {formatWeight(selectedMaterial.metalWeight)}
                  </p>
                  {selectedMaterial.receivedByKarigar && (
                    <div className="mt-2 flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-xs">
                        This material has been received by karigar
                      </span>
                    </div>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <Label htmlFor="deleteReason">Reason *</Label>
              <Textarea
                id="deleteReason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Why is this material being removed?"
                rows={2}
              />
            </div>
            {selectedMaterial?.receivedByKarigar && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="forceDelete"
                  checked={forceAction}
                  onChange={(e) => setForceAction(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="forceDelete" className="text-sm font-normal">
                  Force delete (material has been received)
                </Label>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMaterial}
              disabled={
                !deleteReason.trim() ||
                (selectedMaterial?.receivedByKarigar && !forceAction) ||
                submitting
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? "Deleting..." : "Delete Material"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Return Dialog */}
      <AlertDialog
        open={showResetReturnDialog}
        onOpenChange={setShowResetReturnDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Return Record</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the return record and wastage calculation. You
              will need to record the return again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="resetReason">Reason *</Label>
            <Textarea
              id="resetReason"
              value={resetReturnReason}
              onChange={(e) => setResetReturnReason(e.target.value)}
              placeholder="Why is the return being reset?"
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetReturn}
              disabled={!resetReturnReason.trim() || submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? "Resetting..." : "Reset Return"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Material Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Material Details</DialogTitle>
          </DialogHeader>
          {selectedMaterial && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Metal Type</Label>
                  <p className="font-medium">
                    {selectedMaterial.metalType?.replace(/_/g, " ")}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Weight</Label>
                  <p className="font-medium">
                    {formatWeight(selectedMaterial.metalWeight)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Purity</Label>
                  <p className="font-medium">
                    {selectedMaterial.metalPurity
                      ? `${selectedMaterial.metalPurity}%`
                      : "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Lot Number</Label>
                  <p className="font-medium">
                    {selectedMaterial.lotNumber || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Issued At</Label>
                  <p className="font-medium">
                    {selectedMaterial.issuedAt
                      ? format(
                          new Date(selectedMaterial.issuedAt),
                          "dd MMM yyyy, hh:mm a"
                        )
                      : "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Issued By</Label>
                  <p className="font-medium">
                    {selectedMaterial.issuedByName || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge
                    className={
                      selectedMaterial.receivedByKarigar
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }
                  >
                    {selectedMaterial.receivedByKarigar
                      ? "Received by Karigar"
                      : "Pending Receipt"}
                  </Badge>
                </div>
                {selectedMaterial.receivedAt && (
                  <div>
                    <Label className="text-muted-foreground">Received At</Label>
                    <p className="font-medium">
                      {format(
                        new Date(selectedMaterial.receivedAt),
                        "dd MMM yyyy, hh:mm a"
                      )}
                    </p>
                  </div>
                )}
              </div>

              {selectedMaterial.stones?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Stones</Label>
                  <div className="mt-2 space-y-2">
                    {selectedMaterial.stones.map((stone, idx) => (
                      <div key={idx} className="p-2 bg-muted rounded text-sm">
                        <span className="font-medium">
                          {stone.type?.replace(/_/g, " ")}
                        </span>
                        {stone.quantity && <span> × {stone.quantity}</span>}
                        {stone.weight && <span> ({stone.weight} ct)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedMaterial.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1 p-2 bg-muted rounded">
                    {selectedMaterial.notes}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailsDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// ISSUE MATERIAL DIALOG
// ============================================================================

const IssueMaterialDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  job,
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      metalType: "GOLD_22K",
      metalWeight: "",
      metalPurity: "",
      lotNumber: "",
      stones: [],
      notes: "",
    },
  });

  const {
    fields: stoneFields,
    append: appendStone,
    remove: removeStone,
  } = useFieldArray({
    control,
    name: "stones",
  });

  const watchMetalType = watch("metalType");

  useEffect(() => {
    if (watchMetalType && METAL_PURITY_MAP[watchMetalType]) {
      reset((prev) => ({
        ...prev,
        metalPurity: METAL_PURITY_MAP[watchMetalType],
      }));
    }
  }, [watchMetalType, reset]);

  useEffect(() => {
    if (!open) {
      reset({
        metalType: "GOLD_22K",
        metalWeight: "",
        metalPurity: "",
        lotNumber: "",
        stones: [],
        notes: "",
      });
    }
  }, [open, reset]);

  const handleFormSubmit = (data) => {
    const payload = {
      metalType: data.metalType,
      metalWeight: parseFloat(data.metalWeight),
      metalPurity: parseFloat(data.metalPurity) || undefined,
      lotNumber: data.lotNumber || undefined,
      stones:
        data.stones?.length > 0
          ? data.stones.map((s) => ({
              type: s.type,
              quantity: parseInt(s.quantity) || 1,
              weight: parseFloat(s.weight) || undefined,
              quality: s.quality || undefined,
            }))
          : undefined,
      notes: data.notes || undefined,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issue Material to Karigar</DialogTitle>
          <DialogDescription>
            Issue metal and stones for job: {job?.jobNumber}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div>
            <Label>Metal Type *</Label>
            <Controller
              name="metalType"
              control={control}
              rules={{ required: "Metal type is required" }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select metal type" />
                  </SelectTrigger>
                  <SelectContent>
                    {METAL_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.metalType && (
              <p className="text-sm text-red-500 mt-1">
                {errors.metalType.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="metalWeight">Weight (grams) *</Label>
              <Input
                id="metalWeight"
                type="number"
                step="0.001"
                {...register("metalWeight", {
                  required: "Weight is required",
                  min: { value: 0.001, message: "Weight must be positive" },
                })}
                placeholder="0.000"
              />
              {errors.metalWeight && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.metalWeight.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="metalPurity">Purity (%)</Label>
              <Input
                id="metalPurity"
                type="number"
                step="0.1"
                {...register("metalPurity")}
                placeholder="91.6"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="lotNumber">Lot Number (Optional)</Label>
            <Input
              id="lotNumber"
              {...register("lotNumber")}
              placeholder="LOT-2024-001"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Stones (Optional)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  appendStone({ type: "DIAMOND", quantity: 1, weight: "" })
                }
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Stone
              </Button>
            </div>

            {stoneFields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-4 gap-2 mb-2 items-end"
              >
                <div>
                  <Controller
                    name={`stones.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {STONE_TYPES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    placeholder="Qty"
                    className="h-9"
                    {...register(`stones.${index}.quantity`)}
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Carats"
                    className="h-9"
                    {...register(`stones.${index}.weight`)}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeStone(index)}
                  className="h-9 px-2"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Any special notes about this material..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Issuing..." : "Issue Material"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// EDIT MATERIAL DIALOG
// ============================================================================

const EditMaterialDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  material,
  isReceived,
  forceAction,
  setForceAction,
}) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (open && material) {
      reset({
        metalType: material.metalType || "GOLD_22K",
        metalWeight: material.metalWeight?.toString() || "",
        metalPurity: material.metalPurity?.toString() || "",
        lotNumber: material.lotNumber || "",
        notes: material.notes || "",
      });
    }
  }, [open, material, reset]);

  const handleFormSubmit = (data) => {
    const payload = {
      metalType: data.metalType,
      metalWeight: parseFloat(data.metalWeight),
      metalPurity: parseFloat(data.metalPurity) || undefined,
      lotNumber: data.lotNumber || undefined,
      notes: data.notes || undefined,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Material</DialogTitle>
          <DialogDescription>Update material details</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {isReceived && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  This material has been received by karigar
                </span>
              </div>
              <p className="text-xs text-amber-600 mt-1">
                Editing may require reconciliation with physical materials.
              </p>
            </div>
          )}

          <div>
            <Label>Metal Type *</Label>
            <Controller
              name="metalType"
              control={control}
              rules={{ required: "Metal type is required" }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select metal type" />
                  </SelectTrigger>
                  <SelectContent>
                    {METAL_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="editMetalWeight">Weight (grams) *</Label>
              <Input
                id="editMetalWeight"
                type="number"
                step="0.001"
                {...register("metalWeight", {
                  required: "Weight is required",
                  min: { value: 0.001, message: "Weight must be positive" },
                })}
                placeholder="0.000"
              />
              {errors.metalWeight && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.metalWeight.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="editMetalPurity">Purity (%)</Label>
              <Input
                id="editMetalPurity"
                type="number"
                step="0.1"
                {...register("metalPurity")}
                placeholder="91.6"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="editLotNumber">Lot Number</Label>
            <Input
              id="editLotNumber"
              {...register("lotNumber")}
              placeholder="LOT-2024-001"
            />
          </div>

          <div>
            <Label htmlFor="editNotes">Notes</Label>
            <Textarea
              id="editNotes"
              {...register("notes")}
              placeholder="Any notes..."
              rows={2}
            />
          </div>

          {isReceived && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="forceUpdate"
                checked={forceAction}
                onChange={(e) => setForceAction(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="forceUpdate" className="text-sm font-normal">
                Force update (material has been received)
              </Label>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={(isReceived && !forceAction) || submitting}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// RECORD RETURN DIALOG
// ============================================================================

const RecordReturnDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  job,
  totalIssuedWeight,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      finishedWeight: "",
      unusedMetal: "",
      finishedPurity: "",
      notes: "",
    },
  });

  const watchFinishedWeight = watch("finishedWeight");
  const watchUnusedMetal = watch("unusedMetal");

  const finishedWeight = parseFloat(watchFinishedWeight) || 0;
  const unusedMetal = parseFloat(watchUnusedMetal) || 0;
  const totalReturned = finishedWeight + unusedMetal;
  const wastage = totalIssuedWeight - totalReturned;
  const wastagePercentage = calculateWastagePercentage(
    totalIssuedWeight,
    totalReturned
  );

  useEffect(() => {
    if (!open) {
      reset({
        finishedWeight: "",
        unusedMetal: "",
        finishedPurity: "",
        notes: "",
      });
    }
  }, [open, reset]);

  const handleFormSubmit = (data) => {
    const payload = {
      finishedWeight: parseFloat(data.finishedWeight),
      unusedMetal: parseFloat(data.unusedMetal) || 0,
      finishedPurity: data.finishedPurity || undefined,
      notes: data.notes || undefined,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Material Return</DialogTitle>
          <DialogDescription>
            Record the finished product and unused material returned
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="p-3 bg-blue-50 rounded-lg text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Issued:</span>
              <span className="font-medium">
                {formatWeight(totalIssuedWeight)}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="finishedWeight">
              Finished Product Weight (g) *
            </Label>
            <Input
              id="finishedWeight"
              type="number"
              step="0.001"
              {...register("finishedWeight", {
                required: "Finished weight is required",
                min: { value: 0.001, message: "Weight must be positive" },
              })}
              placeholder="0.000"
            />
            {errors.finishedWeight && (
              <p className="text-sm text-red-500 mt-1">
                {errors.finishedWeight.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="unusedMetal">Unused Metal Returned (g)</Label>
            <Input
              id="unusedMetal"
              type="number"
              step="0.001"
              {...register("unusedMetal")}
              placeholder="0.000"
            />
          </div>

          <div>
            <Label htmlFor="finishedPurity">Finished Purity</Label>
            <Input
              id="finishedPurity"
              {...register("finishedPurity")}
              placeholder="22K, 916, etc."
            />
          </div>

          {(finishedWeight > 0 || unusedMetal > 0) && (
            <div
              className={`p-3 rounded-lg ${
                parseFloat(wastagePercentage) > 5
                  ? "bg-red-50 border border-red-200"
                  : "bg-green-50 border border-green-200"
              }`}
            >
              <p className="text-sm font-medium mb-2">Wastage Calculation</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Returned:</span>
                  <span className="ml-2 font-medium">
                    {formatWeight(totalReturned)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Wastage:</span>
                  <span className="ml-2 font-medium">
                    {formatWeight(wastage > 0 ? wastage : 0)}
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <Badge className={getWastageStatus(wastagePercentage).color}>
                  {wastagePercentage}% wastage -{" "}
                  {getWastageStatus(wastagePercentage).label}
                </Badge>
              </div>
              {parseFloat(wastagePercentage) > 5 && (
                <div className="flex items-center gap-2 mt-2 text-red-700 text-xs">
                  <AlertTriangle className="h-3 w-3" />
                  <span>High wastage - may require review</span>
                </div>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="returnNotes">Notes (Optional)</Label>
            <Textarea
              id="returnNotes"
              {...register("notes")}
              placeholder="Any notes about the return..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Recording..." : "Record Return"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// EDIT RETURN DIALOG
// ============================================================================

const EditReturnDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  returnRecord,
  totalIssuedWeight,
  adjustReason,
  setAdjustReason,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (open && returnRecord) {
      reset({
        finishedWeight: returnRecord.finishedWeight?.toString() || "",
        unusedMetal: returnRecord.unusedMetal?.toString() || "",
        finishedPurity: returnRecord.finishedPurity || "",
        notes: returnRecord.notes || "",
      });
    }
  }, [open, returnRecord, reset]);

  const watchFinishedWeight = watch("finishedWeight");
  const watchUnusedMetal = watch("unusedMetal");

  const finishedWeight = parseFloat(watchFinishedWeight) || 0;
  const unusedMetal = parseFloat(watchUnusedMetal) || 0;
  const totalReturned = finishedWeight + unusedMetal;
  const wastage = totalIssuedWeight - totalReturned;
  const wastagePercentage = calculateWastagePercentage(
    totalIssuedWeight,
    totalReturned
  );

  const handleFormSubmit = (data) => {
    const payload = {
      finishedWeight: parseFloat(data.finishedWeight),
      unusedMetal: parseFloat(data.unusedMetal) || 0,
      finishedPurity: data.finishedPurity || undefined,
      notes: data.notes || undefined,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Material Return</DialogTitle>
          <DialogDescription>
            Update the return record and recalculate wastage
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="p-3 bg-blue-50 rounded-lg text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Issued:</span>
              <span className="font-medium">
                {formatWeight(totalIssuedWeight)}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="editFinishedWeight">
              Finished Product Weight (g) *
            </Label>
            <Input
              id="editFinishedWeight"
              type="number"
              step="0.001"
              {...register("finishedWeight", {
                required: "Finished weight is required",
                min: { value: 0.001, message: "Weight must be positive" },
              })}
              placeholder="0.000"
            />
            {errors.finishedWeight && (
              <p className="text-sm text-red-500 mt-1">
                {errors.finishedWeight.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="editUnusedMetal">Unused Metal Returned (g)</Label>
            <Input
              id="editUnusedMetal"
              type="number"
              step="0.001"
              {...register("unusedMetal")}
              placeholder="0.000"
            />
          </div>

          <div>
            <Label htmlFor="editFinishedPurity">Finished Purity</Label>
            <Input
              id="editFinishedPurity"
              {...register("finishedPurity")}
              placeholder="22K, 916, etc."
            />
          </div>

          {(finishedWeight > 0 || unusedMetal > 0) && (
            <div
              className={`p-3 rounded-lg ${
                parseFloat(wastagePercentage) > 5
                  ? "bg-red-50 border border-red-200"
                  : "bg-green-50 border border-green-200"
              }`}
            >
              <p className="text-sm font-medium mb-2">Updated Wastage</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Returned:</span>
                  <span className="ml-2 font-medium">
                    {formatWeight(totalReturned)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Wastage:</span>
                  <span className="ml-2 font-medium">
                    {formatWeight(wastage > 0 ? wastage : 0)}
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <Badge className={getWastageStatus(wastagePercentage).color}>
                  {wastagePercentage}% wastage
                </Badge>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="adjustReason">Adjustment Reason *</Label>
            <Textarea
              id="adjustReason"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="Why is the return being adjusted?"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!adjustReason?.trim() || submitting}
            >
              {submitting ? "Saving..." : "Save Adjustment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobMaterialsTab;
